import React, { createContext, useContext, useMemo, useReducer } from 'react';

const PortalsContext = createContext({});

const TYPES = {
  PAGE_ACCESS_SWITCHER: 'PAGE_ACCESS_SWITCHER',
};

const INITIAL_STATE_MAP = {
  [TYPES.PAGE_ACCESS_SWITCHER]: {
    forCastingTool: false,
    forProductId: null,
    forEntityType: null,
    onSelect: () => null,
  },
};

const createInitialState = typesObject =>
  Object.keys(typesObject).reduce((acc, type) => {
    acc[type] = {
      open: false,
      payload: INITIAL_STATE_MAP[type] || {},
    };

    return acc;
  }, {});

const ACTIONS = {
  SHOW: 'SHOW',
  HIDE: 'HIDE',
  UPDATE: 'UPDATE',
  RESET: 'RESET',
};

const portalsReducer = (portalsState, { type, action, state: newState }) => {
  let { open, state } = portalsState[type] || {};

  if (action === ACTIONS.RESET) {
    return createInitialState(Object.keys(portalsState));
  }

  switch (action) {
    case ACTIONS.SHOW: {
      open = true;
      state = newState;
      break;
    }
    case ACTIONS.HIDE: {
      open = false;
      state = INITIAL_STATE_MAP[type];
      break;
    }
    case ACTIONS.UPDATE: {
      state = {
        ...state,
        ...newState,
      };
      break;
    }
    default: {
      break;
    }
  }

  switch (type) {
    case TYPES.PAGE_ACCESS_SWITCHER: {
      return {
        ...portalsState,
        [TYPES.PAGE_ACCESS_SWITCHER]: {
          open,
          state,
        },
      };
    }
    default: {
      return portalsState;
    }
  }
};

const usePortalState = type => {
  const context = useContext(PortalsContext);

  if (type) {
    return {
      state: context?.state?.[type]?.state || {},
      open: context?.state?.[type]?.open || false,
    };
  }

  return context || {};
};

const usePortalActions = type => {
  const { dispatch } = useContext(PortalsContext);

  const actions = useMemo(
    () => ({
      show: state => dispatch({ type, action: ACTIONS.SHOW, state }),
      hide: () => dispatch({ type, action: ACTIONS.HIDE }),
      update: state => dispatch({ type, action: ACTIONS.UPDATE, state }),
      reset: () => dispatch({ action: ACTIONS.RESET }),
    }),
    [dispatch, type],
  );

  return actions;
};

const PortalsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(portalsReducer, TYPES, createInitialState);
  const context = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <PortalsContext.Provider value={context}>{children}</PortalsContext.Provider>;
};

export { TYPES as PORTALS, ACTIONS, usePortalState, usePortalActions };
export default PortalsProvider;
